import { useState, useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

const SOCKET_URL =
  window.location.hostname === 'localhost'
    ? 'ws://localhost:8081/socket.io'
    : window.location.origin;

interface LastEvent {
  event: string;
  data: unknown;
  timestamp: number;
}

interface UseSocketReturn {
  socket: Socket | null;
  connected: boolean;
  lastEvent: LastEvent | null;
  joinSession: (sessionId: string) => void;
  joinPlace: (placeId: string) => void;
  joinBusiness: (businessId: string) => void;
  joinDisplay: (businessId: string) => void;
}

export function useSocket(): UseSocketReturn {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<LastEvent | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('qrhost_token');
    const query = token ? { token } : {};

    const socket = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ['websocket'],
      query,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      reconnectionAttempts: 10,
      randomizationFactor: 0.5,
    });

    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', () => setConnected(false));

    socket.on('session:updated', (data: unknown) => {
      setLastEvent({ event: 'session:updated', data, timestamp: Date.now() });
    });

    socket.on('session:notified', (data: unknown) => {
      setLastEvent({ event: 'session:notified', data, timestamp: Date.now() });
    });

    socket.on('queue:changed', (data: unknown) => {
      setLastEvent({ event: 'queue:changed', data, timestamp: Date.now() });
    });

    socket.on('display:updated', (data: unknown) => {
      setLastEvent({ event: 'display:updated', data, timestamp: Date.now() });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const joinSession = useCallback((sessionId: string) => {
    socketRef.current?.emit('join:session', { session_id: sessionId });
  }, []);

  const joinPlace = useCallback((placeId: string) => {
    socketRef.current?.emit('join:place', { place_id: placeId });
  }, []);

  const joinBusiness = useCallback((businessId: string) => {
    socketRef.current?.emit('staff:join', { business_id: businessId });
  }, []);

  const joinDisplay = useCallback((businessId: string) => {
    socketRef.current?.emit('display:join', { business_id: businessId });
  }, []);

  return {
    socket: socketRef.current,
    connected,
    lastEvent,
    joinSession,
    joinPlace,
    joinBusiness,
    joinDisplay,
  };
}
